import { Box, Button, Grid, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FC } from 'react';

const WHO_NEEDS_TO_COMPLY = [
	{
		title: 'Online Shops and Brands',
		description: 'shipping orders directly to EU consumers, from inside or outside the EU',
	},
	{
		title: 'Marketplace Sellers',
		description:
			'selling on Amazon, eBay, Etsy and similar platforms, which must check your EPR registration',
	},
	{
		title: 'Importers',
		description: 'bringing packaged products into an EU country for sale',
	},
	{
		title: 'Manufacturers',
		description: 'packing or filling products sold in the EU under their own name',
	},
];

const paragraphSx = { fontSize: '1rem', mb: 2 };
const subtitleSx = { fontSize: '1.3rem', fontWeight: 'bold', mt: 2, mb: 1 };

export const PpwrBanner: FC = () => {
	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Paper
					sx={{
						margin: '0px 20px 20px 20px',
						overflowX: 'auto',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						gap: 1,
						backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
						borderRadius: '12px',
						p: '20px',
						maxWidth: '1200px',
					}}
				>
					<Grid container spacing={2}>
						<Grid item xs={12} lg={7}>
							<Typography
								component="p"
								color="secondary"
								sx={{
									fontSize: '.9rem',
									fontWeight: 'bold',
									textTransform: 'uppercase',
									letterSpacing: '.05em',
									mb: 1,
								}}
							>
								EPR/PPWR
							</Typography>
							<Typography
								variant="h2"
								color="primary"
								sx={{ fontSize: '1.6rem', fontWeight: 'bold' }}
							>
								Packaging EPR is now mandatory across the EU.
							</Typography>
							<Typography component="p" color="text.primary" sx={paragraphSx}>
								The Packaging and Packaging Waste Regulation (PPWR) has applied since 12 August
								2026.
							</Typography>

							<Typography variant="h2" color="primary" sx={subtitleSx}>
								What is packaging EPR?
							</Typography>
							<Typography component="p" color="text.primary" sx={paragraphSx}>
								Extended Producer Responsibility (EPR) makes sellers pay for collecting and
								recycling the packaging they put on the EU market. Under the PPWR, this covers both
								product packaging and shipping packaging, such as boxes, mailers, bags and filler.
							</Typography>
							<Typography component="p" color="text.primary" sx={paragraphSx}>
								If you sell to EU consumers, you must register in each EU country you ship to,
								report your packaging volumes and pay national recycling fees. Sellers based outside
								the EU must also appoint an authorised representative in each of those countries. If
								you don&apos;t, you risk fines, blocked marketplace listings and losing access to
								the market.
							</Typography>

							<Typography variant="h2" color="primary" sx={subtitleSx}>
								Who needs to comply with packaging EPR?
							</Typography>
							<Typography component="p" color="text.primary" sx={{ fontSize: '1rem', mb: 1 }}>
								Any company that is the first to make packaged goods available in an EU country.
								This includes:
							</Typography>
							<List
								sx={{
									m: '0 0 0 20px',
									p: 0,
									listStyleType: 'disc',
									'& .MuiListItem-root': {
										display: 'list-item',
										padding: 0,
									},
									'& .MuiListItem-root + .MuiListItem-root': {
										marginTop: 0.5,
									},
									'& .MuiTypography-root': {
										color: 'text.primary',
										fontSize: '1rem',
									},
								}}
							>
								{WHO_NEEDS_TO_COMPLY.map(({ title, description }) => (
									<ListItem key={title}>
										<ListItemText
											primary={
												<>
													<Box component="span" sx={{ fontWeight: 'bold' }}>
														{title}
													</Box>{' '}
													{description}
												</>
											}
										/>
									</ListItem>
								))}
							</List>

							<Typography component="p" color="text.primary" sx={{ ...paragraphSx, mt: 2 }}>
								EAS offers a complete packaging EPR service. We act as your authorised
								representative, register and file across the EU, and pay the fees for you. Your
								sales data comes from your existing EAS integration and your marketplace reports.
								You describe your packaging in everyday terms, not regulatory categories. You get
								one monthly invoice and do very little by hand.
							</Typography>
						</Grid>
						<Grid
							item
							xs={12}
							lg={5}
							sx={{ display: 'flex', alignItems: 'start', justifyContent: 'center' }}
						>
							<img
								src="/img/PPWR-banner-2.webp"
								alt="EAS packaging EPR service across the EU"
								style={{
									width: '100%',
									height: 'auto',
									borderRadius: '12px',
								}}
							/>
						</Grid>
					</Grid>

					<Grid container spacing={1} sx={{ mt: 1, width: '100%' }}>
						<Grid item xs={12} lg={4}>
							<Button
								variant="contained"
								color="primary"
								href="https://form.jotform.com/262092667522056"
								target="_blank"
								fullWidth
							>
								Register for EPR service
							</Button>
						</Grid>
						<Grid item xs={12} lg={4}>
							<Button
								variant="outlined"
								color="primary"
								href="https://scheduler.zoom.us/d/qhz9qlc7/vat-solutions"
								target="_blank"
								fullWidth
							>
								Book a meeting
							</Button>
						</Grid>
						<Grid item xs={12} lg={4}>
							<Button
								variant="outlined"
								color="primary"
								href="https://easproject.com/epr/"
								target="_blank"
								fullWidth
							>
								More about EPR
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Box>
		</Box>
	);
};
